/**
 * The Reach Wire and the legibility UI (M4 plan I6, spec §12, §15 items 1-4 and 7; order `FEATURE_ORDER.wire`
 * 525, after M3's Reach HUD). Runs only while the strategic service is up (the career in the Reach).
 *
 * - **Ticker** (`ticker.ts`): new Wire items (`client.onWire`) queue on a HUD strip at the top right; `frame`
 *   steps it and writes only `transform` and `textContent` (allocation-free).
 * - **Wire panel**: a dashboard section, Intel › Wire (`createWireSection`; W3 card M1), with an unread badge for
 *   notable items. N (bindings `wirePanel`) opens the dashboard there and closes it again through the dashboard's
 *   own deep links (`dashboard.json deepLinks`, on since W3 card M4). Station screens keep their Wire tab (`StationServicePanel`). Choosing an item closes the
 *   dashboard and opens the galaxy map (on the next unpaused frame if the game is paused then).
 * - **Station panel**: a `StationServicePanel` (`economy`), so M3's services panel and M2's station screen show it.
 * - **Market rows**: installs the strategic rows behind M2's market model (`setMarketRowSource`), which adds the
 *   stock bar, trend arrow and cause chip.
 * - **Away report**: the `awayReport` bus event (I2's catch-up flow) opens the report modal.
 * - **Pinned goal** (M10 P5a2b, `goalWire.ts`): the pinned hull's Wire lines through the worker's `postWire`, once
 *   each (`Career.pinnedHull.posted`), and the HUD goal chip; inert while `shipyard.json enabled` is false.
 *
 * `frame` allocates nothing except on the open and close events (D-014).
 */
import type { FrameInfo, GameContext, GameFeature } from '../../app/feature'
import { FEATURE_ORDER } from '../../app/feature'
import { logger } from '../../core/logger'
import type { CareerRuntime } from '../../career/runtime'
import type { Career } from '../../career/types'
import type { M2Runtime } from '../../space/contracts'
import { mountGoalChip } from '../../hud/goal'
import { createPadNav, stepPadNav, type PadNavState } from '../../hud/menu'
import type { StationServiceRegistry } from '../../reach/ports'
import { loadSettingsConfig } from '../../settings/settings'
import type { StrategicClient } from '../../strategic/client'
import type { AwayReport } from '../../strategic/types'
import { strategicMarketFor } from '../../strategic-link/market'
import { loadDashboardConfig } from '../../ui/dashboard/config'
import { openDashboard, registerDashSection } from '../../ui/dashboard/registry'
import { setMarketRowSource } from '../../ui/strategic/marketRows'
import { createOverlayHost, type OverlayHost } from '../../ui/strategic/modal'
import { createWireSection } from '../../ui/strategic/WirePanel'
import { withAwayStoryWarnings } from '../../ui/strategic/awayReportModel'
import { createStrategicPanels } from '../../ui/strategic/panels'
import { systemNameIn } from '../../ui/strategic/names'
import { neighboursOf, type WireFilterContext } from '../../ui/strategic/wireModel'
import mapsFeature from '../maps/feature'
import { createGoalHost, type GoalHost } from './goalWire'
import { createDomTicker, createTickerQueue, loadWireUiConfig, type TickerQueue, type TickerView } from './ticker'
import '../../ui/strategic/strategic.css'

export interface WireFeature extends GameFeature {
  /** True while the strategic service is up and the feature runs. */
  readonly active: boolean
  readonly queue: TickerQueue | null
  readonly overlays: OverlayHost | null
  /** Opens the dashboard at Intel › Wire (the debug API, tests). */
  openWire(): void
  /** Shows an away report (the bus event, tests). */
  showAwayReport(report: AwayReport): void
  frame(ctx: GameContext, f: FrameInfo): void
  dispose(): void
}

export interface WireFeatureOptions {
  /** Opens the galaxy map for a chosen Wire item (default: the maps feature). */
  openMap?: (ctx: GameContext, systemId: string) => void
}

/** The career runtime's write hook (`applyDevCommand`), as `features/fleetPanels` reads it. */
function hasWriteHook(m2: M2Runtime): m2 is M2Runtime & Pick<CareerRuntime, 'applyDevCommand'> {
  return 'applyDevCommand' in m2 && typeof m2.applyDevCommand === 'function'
}

/** Registries the panels already went into (the registry has no removal; a re-init must not register twice). */
const registered = new WeakSet<StationServiceRegistry>()

function defaultOpenMap(ctx: GameContext, systemId: string): void {
  logger.debug('Reach Wire: opening the map for a Wire item', { systemId })
  mapsFeature.open(ctx, systemId)
}

export function createWireFeature(opts: WireFeatureOptions = {}): WireFeature {
  const openMap = opts.openMap ?? defaultOpenMap
  let ctxRef: GameContext | null = null
  let client: StrategicClient | null = null
  let queue: TickerQueue | null = null
  let ticker: TickerView | null = null
  let overlays: OverlayHost | null = null
  let nav: PadNavState | null = null
  let pendingMap: string | null = null
  let goal: GoalHost | null = null
  let goalCareer: (() => Career) | null = null
  let goalChip: HTMLElement | null = null
  /** The last frame's pause state (the pause menu is up). */
  let paused = false
  const offs: (() => void)[] = []

  function filterContext(ctx: GameContext): WireFilterContext {
    const reach = ctx.reach
    const systemId = reach?.session.layout?.systemId ?? null
    const cargo = new Set<string>()
    const m2 = ctx.services.maybe('m2')
    if (m2 !== undefined) {
      const wares = m2.career().cargo.wares
      for (const w of Object.keys(wares) as (keyof typeof wares)[]) if (wares[w] > 0) cargo.add(w)
    }
    return { systemId, neighbours: neighboursOf(reach?.galaxy.relays ?? [], systemId), cargo, systemName: (id) => systemNameIn(ctx.reach?.galaxy, id) }
  }

  function selectSystem(ctx: GameContext, systemId: string): void {
    pendingMap = systemId
    // From the flight the map opens now; from the pause menu on the first unpaused frame.
    if (!paused && ctx.ui.modal === 0) {
      pendingMap = null
      openMap(ctx, systemId)
    }
  }

  function stationName(c: StrategicClient, id: string): string | null {
    const v = c.systemView
    if (v !== null) for (const s of v.stations) if (s.id === id) return s.name
    return null
  }

  const feature: WireFeature = {
    name: 'wire',
    order: FEATURE_ORDER.wire,
    get active(): boolean {
      return client !== null
    },
    get queue(): TickerQueue | null {
      return queue
    },
    get overlays(): OverlayHost | null {
      return overlays
    },

    init(ctx: GameContext): void {
      const c = ctx.services.maybe('strategic')
      if (c === undefined || ctx.reach === null) return
      const cfg = loadWireUiConfig()
      ctxRef = ctx
      client = c
      queue = createTickerQueue(cfg.ticker)
      ticker = createDomTicker(ctx.hudRoot, cfg.ticker, (id) => systemNameIn(ctx.reach?.galaxy, id))
      overlays = createOverlayHost({ root: ctx.overlayRoot, ui: ctx.ui, paused: () => paused })
      nav = createPadNav(loadSettingsConfig().menuNav, ctx.data.bindings.gamepad.buttons.pause)

      const q = queue
      offs.push(c.onWire((item) => q.push(item)))
      offs.push(ctx.bus.on('awayReport', (r) => feature.showAwayReport(r)))
      offs.push(setMarketRowSource((id) => strategicMarketFor(c).rows(id)))
      const wire = createWireSection({
        items: () => c.wire,
        context: () => filterContext(ctx),
        onWire: (fn) => c.onWire(fn),
        onSelect: (id) => selectSystem(ctx, id),
        config: cfg.dashboard,
      })
      offs.push(wire.dispose, registerDashSection(wire.section))

      // M10 P5a2b: the pinned goal's Wire lines and the HUD chip (the career runtime's write hook; keys off: inert).
      const m2 = ctx.services.maybe('m2')
      if (m2 !== undefined && hasWriteHook(m2)) {
        const hud = ctx.hudRoot
        const read = (): Career => m2.career()
        goalCareer = read
        goal = createGoalHost({
          career: read,
          apply: (name, fn) => m2.applyDevCommand(name, fn),
          yard: async (stationId) => {
            const [fleet, station] = await Promise.all([c.fleetView(), c.stationView(stationId)])
            return { corporation: !fleet.soleTrader, owner: station.owner, systemId: station.systemId }
          },
          post: (cmd) => c.command(cmd),
          mount: () => {
            const v = mountGoalChip(hud)
            goalChip = v.el
            return v
          },
        })
      }

      const services = ctx.reach.services
      if (!registered.has(services)) {
        registered.add(services)
        for (const p of createStrategicPanels({ client: c, filterContext: () => filterContext(ctx), onSelectSystem: (id) => selectSystem(ctx, id) })) services.register(p)
      }
    },

    openWire(): void {
      if (client === null) return
      openDashboard(loadDashboardConfig().deepLinkTargets.wirePanel)
    },

    showAwayReport(report: AwayReport): void {
      const c = client
      if (c === null || overlays === null) return
      // M7 (S35): the story's countdown warnings for this absence (Okafor's and the like), merged once; held floors are re-sent.
      const story = ctxRef?.services.maybe('story')
      const full = story === undefined ? report : withAwayStoryWarnings(report, story.catchUp(report.simulatedDays))
      overlays.openAway(full, (id) => stationName(c, id), (id) => systemNameIn(ctxRef?.reach?.galaxy, id))
    },

    frame(ctx: GameContext, f: FrameInfo): void {
      const q = queue
      if (q === null || ticker === null || overlays === null || nav === null) return
      paused = f.paused
      q.step(f.paused ? 0 : f.dt)
      ticker.sync(q)
      // A new Career reference only does work (Careers are immutable); an unchanged frame is one compare.
      if (goal !== null && goalCareer !== null) goal.careerChanged(goalCareer())

      // The pad reader runs every frame so its edges stay current (menu.ts); inputs go to an open overlay only.
      const i = stepPadNav(nav, ctx.input.pad, f.dt)
      if (overlays.open !== null && i !== null) overlays.input(i)
      if (f.paused) return
      if (pendingMap !== null && ctx.ui.modal === 0) {
        const id = pendingMap
        pendingMap = null
        openMap(ctx, id)
      }
    },

    dispose(): void {
      for (const off of offs.splice(0)) off()
      overlays?.dispose()
      ticker?.dispose()
      goal?.dispose()
      goalChip?.remove()
      goal = null
      goalCareer = null
      goalChip = null
      overlays = null
      ticker = null
      queue = null
      client = null
      ctxRef = null
      nav = null
      pendingMap = null
    },
  }
  return feature
}

const feature: WireFeature = createWireFeature()

export default feature
